import dayjs from "dayjs";
import React, { useState } from "react";
import { FullPageLayout } from "../../../components/FullPageLayout"
import { Container } from "../../common/modules.styles";
import { axiosInstance } from "../../../utils/axios";
import toast from "react-hot-toast";
import { Typeahead } from "react-bootstrap-typeahead";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { handleAxiosError } from "../../../utils/functions/handleAxiosError";

function useData() {
    const [recipes, setRecipes] = useState<{ id: number, name: string }[]>([]);
    
    const fetchRecipes = async () => {
        try {
            const response = await axiosInstance('/recipes/get-recipes');
            setRecipes(response.data);
        } catch (error) {
            console.error("Error fetching recipes", error);
        }
    }

    React.useEffect(() => {
        fetchRecipes();
    }, []);

    return { recipes };
}

type Fill = {
    id: number;
    amount: number;
    date: string;
    product: {
        name: string;
    }
    filledBy: { username: string } | null;
}

type Production = {
    id: number;
    date: string;
    state: string;
    recipe: {
        name: string;
    }
    fills: Fill[];
}

export default function ProductionsHistoryPage() {
    const {recipes} = useData();
    const [recipe, setRecipe] = useState<{ id: number, name: string }[]>([]);
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState(dayjs().format('YYYY-MM-DD'));
    const [results, setResults] = useState<Production[] | null>(null);

    const fetchResults = async () => {
        try {
            const response = await axiosInstance.get('/productions/productions-history', {
                params: {
                    recipeId: recipe.length > 0 ? recipe[0].id : 'all',
                    desde,
                    hasta   
                }
            });
            setResults(response.data);
        } catch (error) {
            handleAxiosError(error);
        }
    }

    const handleFilter = async (e: React.FormEvent) => {
        e.preventDefault();
        await fetchResults();
    }

    return (
        <FullPageLayout>
            <Container>
                <h2>Historial de Producciones</h2>
                <div id="filters-form">
                    <form onSubmit={handleFilter} style={{ display: "flex", gap: "1rem", flex: 1 }}>
                        <div className="mb-3" style={{ flex: 1 }}>
                            <label htmlFor="recipe" className="form-label">Receta</label>
                            <Typeahead
                                id="recipe"
                                labelKey="name"
                                onChange={setRecipe}
                                options={recipes}
                                placeholder="Seleccione una receta..."
                                selected={recipe}
                            />
                        </div>
                        <div className="mb-3" style={{ flex: 1 }}>
                            <label htmlFor="startDate" className="form-label">Fecha de inicio</label>
                            <input type="date" className="form-control" id="startDate" value={desde} onChange={(e) => setDesde(e.target.value)} />
                        </div>
                        <div className="mb-3" style={{ flex: 1 }}>
                            <label htmlFor="endDate" className="form-label">Fecha de fin</label>
                            <input type="date" className="form-control" id="endDate" value={hasta} onChange={(e) => setHasta(e.target.value)} required />
                        </div>
                        <button  type="submit" className="mb-3 btn btn-primary">Filtrar</button>
                    </form>
                </div>
                <div id="history-table" style={{overflowY: "scroll"}}>
                    {results ? (
                        results.length > 0 ? (
                            results.map((production : Production) => (
                                <ProductionsHistoryItem key={production.id} production={production} refreshResults={fetchResults} />
                            ))
                        ) : (
                            <p>No se encontraron producciones con los filtros aplicados.</p>
                        )
                    ) : null}
                </div>
            </Container>
        </FullPageLayout>
    );
}

const ProductionHistoryItem = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin-bottom: 1rem;
`;

const ProductionInfo = styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    align-items: center;
    padding: 0.5rem;
    border-bottom: 1px solid #ccc;
    background-color: #f9f9f9;
    &:hover { background-color: #e0e0e0; cursor: pointer; }
`;

const FillingHistory = styled.div`
    padding: 0 0.5rem;
`;

const FillItem = styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        &:not(:first-child) {
            border-top: 1px solid #eee;
        }
`;

const FillActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
`;

const ResetFillButton = styled.button`
    border: none;
    background: transparent;
    color: #c0392b;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        color: #9aa0a6;
        cursor: not-allowed;
    }
`;

function ProductionsHistoryItem({ production, refreshResults }: { production: Production, refreshResults: () => Promise<void> }) {
    const [showed, setShowed] = useState(false);
    const [fillToUndo, setFillToUndo] = useState<Fill | null>(null);
    const [confirmationText, setConfirmationText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const toggleShowed = () => setShowed(!showed);

    const closeUndoModal = () => {
        if (submitting) return;
        setFillToUndo(null);
        setConfirmationText('');
    }

    const handleUndoFill = async () => {
        if (!fillToUndo || confirmationText.trim().toLowerCase() !== 'deshacer') {
            return;
        }

        setSubmitting(true);
        try {
            await axiosInstance.post('/productions/unfill-production', {
                productionId: production.id,
                fillId: fillToUndo.id,
            });
            toast.success('Llenado deshecho correctamente');
            closeUndoModal();
            await refreshResults();
        } catch (error) {
            handleAxiosError(error);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <ProductionHistoryItem id="production-history-item">
                <ProductionInfo onClick={toggleShowed}>
                    <div>{dayjs(production.date).format('DD-MM-YYYY')}</div>
                    <div>{production.recipe.name}</div>
                    <div>{production.state}</div>
                </ProductionInfo>
                <FillingHistory id="filling-history" style={{ display: showed ? 'block' : 'none' }}>
                    {production.fills?.map((fill: Fill) => (
                        <FillItem key={fill.id}>
                            <div>{dayjs(fill.date).format('DD-MM-YYYY')}</div>
                            <div><strong>{fill.amount}</strong> {fill.product.name || "N/A"}</div>
                            <FillActions>
                                <span>{fill.filledBy?.username || "N/A"}</span>
                                <ResetFillButton
                                    type="button"
                                    title="Deshacer llenado"
                                    aria-label="Deshacer llenado"
                                    onClick={() => setFillToUndo(fill)}
                                    disabled={submitting}
                                >
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                </ResetFillButton>
                            </FillActions>
                        </FillItem>
                    ))}
                </FillingHistory>
            </ProductionHistoryItem>

            <Modal show={fillToUndo !== null} onHide={closeUndoModal} backdrop={submitting ? 'static' : true} keyboard={!submitting}>
                <Modal.Header closeButton={!submitting}>
                    <Modal.Title>Deshacer llenado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: '0.5rem' }}>
                        Para confirmar, escriba la palabra <strong>deshacer</strong>.
                    </p>
                    {fillToUndo && (
                        <p style={{ marginBottom: '1rem' }}>
                            Se revertirá el llenado de <strong>{fillToUndo.amount}</strong> {fillToUndo.product.name || 'N/A'} registrado el {dayjs(fillToUndo.date).format('DD-MM-YYYY')}.
                        </p>
                    )}
                    <input
                        type="text"
                        className="form-control"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder="Escriba deshacer"
                        disabled={submitting}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={closeUndoModal} disabled={submitting}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleUndoFill}
                        disabled={submitting || confirmationText.trim().toLowerCase() !== 'deshacer'}
                    >
                        {submitting ? 'Confirmando...' : 'Confirmar'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}